import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useEmpresasContext } from '../context/EmpresasContext';

// ===============================
// COMPONENTE SELECTOR DE EMPRESA
// ===============================

interface EmpresaSelectorProps {
  className?: string;
  variant?: 'dropdown' | 'modal' | 'compact';
  showOnSingle?: boolean; // Mostrar incluso si solo hay 1 empresa
}

export const EmpresaSelector: React.FC<EmpresaSelectorProps> = ({
  className = '',
  variant = 'dropdown',
  showOnSingle = false
}) => {
  const { t } = useTranslation();
  const {
    misEmpresas,
    empresaActiva,
    tieneMultiplesEmpresas,
    cargando,
    cambiarEmpresaConInvalidacion, // 🔄 CAMBIO: Usar el método con invalidación
    nombreEmpresaActiva,
    esAdminEmpresa
  } = useEmpresasContext();

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // ===============================
  // MANEJO DE CLICKS FUERA
  // ===============================

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // ===============================
  // VALIDACIONES
  // ===============================

  // No mostrar si está cargando
  if (cargando) {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
        <span className="text-sm text-gray-600">{t('common.loading')}</span>
      </div>
    );
  }

  // No mostrar si no hay empresas
  if (!misEmpresas || misEmpresas.length === 0) {
    return (
      <div className={`text-red-600 text-sm ${className}`}>
        ❌ Sin empresas asignadas
      </div>
    );
  }

  // No mostrar si solo hay 1 empresa y showOnSingle es false
  if (!tieneMultiplesEmpresas && !showOnSingle) {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <span className="text-green-600">🏢</span>
        <span className="text-sm font-medium text-gray-900">
          {nombreEmpresaActiva}
        </span>
        {esAdminEmpresa && (
          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
            Admin
          </span>
        )}
      </div>
    );
  }

  // ===============================
  // MANEJO DE CAMBIO DE EMPRESA
  // ===============================

  const handleCambiarEmpresa = async (empresaId: number) => {
    
    if (empresaId === empresaActiva?.empresa.id) {
      setIsOpen(false);
      return;
    }

    try {
      await cambiarEmpresaConInvalidacion(empresaId); // 🔄 CAMBIO: Usar el método con invalidación
      setIsOpen(false);
    } catch (error) {
      console.error('🔍 ❌ Error al cambiar empresa:', error);
    }
  };

  // ===============================
  // RENDER SEGÚN VARIANTE
  // ===============================

  if (variant === 'compact') {
    return (
      <div className={`text-sm ${className}`}>
        <span className="text-gray-600">{t('admin.companies')}:</span>{' '}
        <span className="font-medium text-gray-900">{nombreEmpresaActiva}</span>
        {tieneMultiplesEmpresas && (
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="ml-2 text-blue-600 hover:text-blue-800 text-xs"
          >
            (cambiar)
          </button>
        )}
      </div>
    );
  }

  if (variant === 'dropdown') {
    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        {/* Botón principal */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-2 px-3 py-2 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
        >
          <span className="text-green-600">🏢</span>
          <div className="flex flex-col items-start">
            <span className="text-sm font-medium text-gray-900">
              {nombreEmpresaActiva}
            </span>
            {tieneMultiplesEmpresas && (
              <span className="text-xs text-gray-500">
                {misEmpresas?.length || 0} empresas disponibles
              </span>
            )}
          </div>
          {tieneMultiplesEmpresas && (
            <svg
              className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </button>

        {/* Dropdown */}
        {isOpen && tieneMultiplesEmpresas && misEmpresas && (
          <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
            {misEmpresas.map((empresa, index) => (
              <button
                key={empresa.empresa_id || `empresa-${index}`}  // ✅ CORREGIDO: usar empresa_id
                onClick={() => handleCambiarEmpresa(empresa.empresa_id)}  // ✅ CORREGIDO: usar empresa_id
                className={`w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors ${
                  empresa.empresa_id === empresaActiva?.empresa.id  // ✅ CORREGIDO: empresa_id vs empresa.id
                    ? 'bg-blue-50 text-blue-900'
                    : 'text-gray-900'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium">{empresa.nombre}</p>
                    <p className="text-xs text-gray-500">Organización UAU</p>  {/* 🔄 CORREGIDO */}
                  </div>
                  <div className="flex items-center space-x-2">
                    {esAdminEmpresa && (
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                        Admin
                      </span>
                    )}
                    {empresa.empresa_id === empresaActiva?.empresa.id && (
                      <span className="text-blue-600">✓</span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // variant === 'modal' - Para implementar más adelante si es necesario
  return null;
};

// ===============================
// COMPONENTE SIMPLE PARA HEADER
// ===============================

export const EmpresaEnHeader: React.FC = () => {
  const { nombreEmpresaActiva, tieneMultiplesEmpresas } = useEmpresasContext();

  if (!tieneMultiplesEmpresas) {
    return (
      <div className="flex items-center space-x-2">
        <span className="text-green-600">🏢</span>
        <span className="text-sm text-gray-600">{nombreEmpresaActiva}</span>
      </div>
    );
  }

  return <EmpresaSelector variant="compact" className="text-gray-600" />;
};

export default EmpresaSelector;
